import { createFileRoute } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'

import {
  getListOrphanedPaymentsQueryKey,
  getListUnsettledSessionsQueryKey,
  useAdminRefundOrphanedPayment,
  useListOrphanedPayments,
  useListUnsettledSessions,
  useMarkBookingPaid,
} from '../../../api/generated/endpoints/admin/admin'
import { apiMessage } from '../../../lib/api-error'
import { formatUYU } from '../../../lib/money'
import { SIN_NOMBRE } from '../../../lib/admin-calendar'
import { formatDayLong, formatShortInstant } from '../../../lib/time'

/**
 * Who still owes money, and -- underneath -- payments that lost their bay.
 *
 * The facility takes money directly now: the coach is handed cash or a
 * transfer, so the question this screen exists to answer is "who have I not
 * been paid by?". Sessions that happened and that nobody settled, oldest
 * first, each with the one button that closes it.
 *
 * Settled from HERE rather than by opening each booking. It is a worklist, so
 * one tap per line beats two navigations -- and the booking screen reads from
 * the calendar cache, which spans -30/+60 days, so anything older than a month
 * could not be opened from here at all.
 *
 * ## The orphan list below
 *
 * Payments that lost their bay, and what became of them.
 *
 * A permanent screen rather than one that appears when something goes wrong.
 * The whole point of this ticket is that stranded money should be FINDABLE, and
 * a surface the owner has never seen before the day it matters is not findable
 * — it is a page they have to be told about while a customer waits.
 *
 * Resolved rows stay, because "what happened to that one?" is the question that
 * actually gets asked, and a screen showing only open problems sends the owner
 * back to reading the table by hand.
 *
 * `notified` is the column worth looking at. A customer with no email address
 * is never told their money is stranded — correct for the outbox, useless here
 * — so it is stated rather than left as a silent absence. That row is the phone
 * call, and the refund button beside it is how it ends.
 *
 * Kept, but hidden while empty. It can only be populated by Mercado Pago,
 * which is switched off, so a permanently empty panel above the thing he
 * actually came for would train him to scroll past this screen. The reasoning
 * for it being a permanent surface still holds the day payments come back.
 */
export const Route = createFileRoute('/_authed/_admin/admin/payments')({
  component: AdminPaymentsPage,
})

function AdminPaymentsPage() {
  const queryClient = useQueryClient()
  // Not destructured: see the narrowing note in book.tsx.
  const orphans = useListOrphanedPayments({ include_resolved: true })

  const refund = useAdminRefundOrphanedPayment({
    mutation: {
      retry: false,
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: getListOrphanedPaymentsQueryKey() }),
    },
  })

  if (orphans.isPending) return <p className="text-slate-600">Cargando…</p>
  if (orphans.isError) {
    return (
      <p role="alert" className="text-red-600">
        No se pudo cargar la lista de pagos.
      </p>
    )
  }

  const items = orphans.data.items

  return (
    <div className="space-y-10">
      <UnsettledPanel />

      {/* Hidden while empty rather than shown as a reassuring nil. Only Mercado
          Pago can fill it and Mercado Pago is off, so a permanently empty panel
          under the worklist would teach him to stop scrolling. */}
      {items.length > 0 && (
        <div className="space-y-6">
          <div>
            <h2 className="text-2xl font-semibold">Pagos sin horario</h2>
            <p className="mt-1 text-slate-600">
              Pagos aprobados para un horario que ya estaba ocupado. Cada uno se resuelve eligiendo
              otro horario o devolviendo el importe.
            </p>
          </div>
          {items.length > 0 && (
            // Cards rather than a table: the admin panel is an iPad and a seven
            // column table is the thing that scrolls sideways on it.
            <ul className="space-y-4">
              {items.map((item) => {
                const open = item.resolved_at === null
                return (
                  <li
                    key={item.id}
                    className="space-y-3 rounded-lg border border-slate-200 bg-white p-4"
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <p className="text-lg font-semibold tabular-nums">
                        {formatUYU(item.amount_uyu_pesos)}
                      </p>
                      <span
                        className={[
                          'rounded-full px-3 py-1 text-xs font-semibold',
                          open ? 'bg-amber-100 text-amber-900' : 'bg-slate-100 text-slate-600',
                        ].join(' ')}
                      >
                        {open
                          ? item.days_left === 1
                            ? 'Sin resolver — queda 1 día'
                            : `Sin resolver — quedan ${item.days_left} días`
                          : item.status === 'refunded'
                            ? 'Devuelto'
                            : 'Reasignado a otro horario'}
                      </span>
                    </div>

                    <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
                      <div className="flex justify-between gap-4 sm:block">
                        <dt className="text-slate-500">Cliente</dt>
                        <dd className="font-medium">{item.customer_name ?? 'Sin nombre'}</dd>
                      </div>
                      <div className="flex justify-between gap-4 sm:block">
                        <dt className="text-slate-500">Teléfono</dt>
                        <dd className="font-medium">{item.customer_phone ?? '—'}</dd>
                      </div>
                      <div className="flex justify-between gap-4 sm:block">
                        <dt className="text-slate-500">Horario perdido</dt>
                        <dd className="font-medium">
                          {formatDayLong(item.lost_date)}, {item.lost_start} — {item.lost_bay_name}
                        </dd>
                      </div>
                      {/* Only while it is still open. A resolved row showing a
                      future auto-refund date says the money is about to move
                      when it already has, or never will. */}
                      <div className="flex justify-between gap-4 sm:block">
                        <dt className="text-slate-500">
                          {open ? 'Se devuelve solo el' : 'Resuelto'}
                        </dt>
                        <dd className="font-medium">
                          {open
                            ? formatDayLong(item.refund_due_at.slice(0, 10))
                            : item.resolved_at
                              ? formatShortInstant(item.resolved_at)
                              : '—'}
                        </dd>
                      </div>
                      <div className="flex justify-between gap-4 sm:block">
                        <dt className="text-slate-500">Quedó sin horario</dt>
                        <dd className="font-medium">{formatShortInstant(item.orphaned_at)}</dd>
                      </div>
                      <div className="flex justify-between gap-4 sm:block">
                        <dt className="text-slate-500">Aviso al cliente</dt>
                        <dd className="font-medium">
                          {item.notified ? (
                            <span className="break-all">{item.customer_email}</span>
                          ) : (
                            // The one that needs a human. Said out loud, because an
                            // absent row and a notified one look identical.
                            <span className="text-amber-800">
                              Sin correo — hay que avisar por teléfono
                            </span>
                          )}
                        </dd>
                      </div>
                    </dl>

                    {open && (
                      <button
                        type="button"
                        onClick={() => refund.mutate({ paymentId: item.id })}
                        disabled={refund.isPending}
                        className="inline-flex min-h-12 items-center rounded-lg border border-slate-300 px-4 font-medium text-slate-900 disabled:opacity-50"
                      >
                        Devolver el importe ahora
                      </button>
                    )}
                  </li>
                )
              })}
            </ul>
          )}

          {refund.isError && (
            <p role="alert" className="text-sm text-red-600">
              {apiMessage(refund.error, 'No se pudo devolver el importe.')}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * The worklist: sessions that happened and that nobody has paid for.
 *
 * Oldest first, because the one from three weeks ago is the one he is most
 * likely to have forgotten and least likely to collect. `days_ago` is printed
 * rather than left to be worked out from the date -- "hace 12 días" is the
 * thing that decides whether he picks up the phone.
 *
 * One tap settles it, and it means efectivo, the same assumption HOY makes.
 * Anything else -- transferencia, a different amount -- is on the booking's own
 * screen, which is one tap further for the case that is rarer.
 */
function UnsettledPanel() {
  const queryClient = useQueryClient()
  const owed = useListUnsettledSessions()
  const markPaid = useMarkBookingPaid({
    mutation: {
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: getListUnsettledSessionsQueryKey() }),
    },
  })

  if (owed.isPending) return <p className="text-slate-600">Cargando…</p>
  if (owed.isError) {
    return (
      <p role="alert" className="text-red-600">
        No se pudo cargar lo que está sin cobrar.
      </p>
    )
  }

  const { items, total_uyu_pesos: total } = owed.data

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Sin cobrar</h1>
        <p className="mt-1 text-slate-600">Sesiones que ya pasaron y que todavía nadie pagó.</p>
      </div>

      {items.length === 0 ? (
        <p className="rounded-lg border border-slate-200 bg-white p-4 text-slate-600">
          No queda nada sin cobrar.
        </p>
      ) : (
        <>
          <p className="text-2xl font-semibold tabular-nums">
            {formatUYU(total)}{' '}
            <span className="text-lg font-normal text-slate-600">
              en {items.length === 1 ? '1 sesión' : `${items.length} sesiones`}
            </span>
          </p>

          <ul className="space-y-4">
            {items.map((item) => (
              <li
                key={item.id}
                className="flex flex-wrap items-center justify-between gap-4 rounded-lg border-2 border-amber-500 bg-amber-50 p-5"
              >
                <div>
                  <p className="text-xl font-medium">{item.customer_name ?? SIN_NOMBRE}</p>
                  <p className="text-lg tabular-nums">
                    {formatDayLong(item.date)} · {item.start} · {item.bay_name}
                  </p>
                  <p className="text-base text-amber-900">
                    {item.days_ago === 0
                      ? 'Hoy'
                      : item.days_ago === 1
                        ? 'Hace 1 día'
                        : `Hace ${item.days_ago} días`}
                    {item.customer_phone ? ` · ${item.customer_phone}` : ''}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-4">
                  <span className="text-xl font-semibold tabular-nums">
                    {formatUYU(item.price_uyu_pesos)}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      markPaid.mutate({ bookingId: item.id, data: { method: 'efectivo' } })
                    }
                    disabled={markPaid.isPending}
                    className="min-h-16 rounded-md bg-slate-900 px-6 text-xl text-white focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900 disabled:bg-slate-400"
                  >
                    Cobré en efectivo
                  </button>
                </div>
              </li>
            ))}
          </ul>

          {markPaid.isError && (
            <p role="alert" className="text-lg text-red-700">
              {apiMessage(markPaid.error, 'No se pudo registrar el cobro.')}
            </p>
          )}
        </>
      )}
    </div>
  )
}
